// DocumentsPanel — upload UI for borrowers + view/download for all authorized roles.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, errorMessage, API, getToken } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Upload, Download, FileText } from "lucide-react";
import { fmtDateTime } from "../lib/constants";
import { toast } from "sonner";

const DOC_TYPES = [
  { key: "executed_contract", label: "Executed Contract" },
  { key: "drivers_license", label: "Driver's License / ID" },
  { key: "credit_report", label: "Credit Report" },
  { key: "bank_statement", label: "Bank Statements" },
  { key: "purchase_agreement", label: "Purchase Agreement" },
  { key: "rehab_budget", label: "Rehab Budget" },
  { key: "proof_of_insurance", label: "Proof of Insurance" },
  { key: "entity_documents", label: "Entity Documents" },
  { key: "title_report", label: "Title Report" },
  { key: "appraisal", label: "Appraisal" },
  { key: "other", label: "Other" },
];
const LABEL = Object.fromEntries(DOC_TYPES.map((d) => [d.key, d.label]));
const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif";

export default function DocumentsPanel({ applicationId, canUpload, title = "Documents", subtitle }) {
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("executed_contract");
  const [customLabel, setCustomLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/documents/application/${applicationId}`);
      setDocs(data);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [applicationId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File too large (max 15MB)");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", docType);
      fd.append("file", file);
      if (customLabel.trim()) fd.append("label", customLabel.trim());
      await api.post(`/documents/application/${applicationId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${LABEL[docType]} uploaded`);
      setCustomLabel("");
      await load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (doc) => {
    try {
      const resp = await fetch(`${API}/documents/${doc.document_id}/download`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <Card className="border-slate-800 bg-slate-900 p-6" data-testid="documents-panel">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold">{title}</div>
          {subtitle && <div className="mt-1 text-xs text-slate-500">{subtitle}</div>}
        </div>
        <Badge variant="outline" className="bg-slate-500/10 text-slate-300 border-slate-500/20">
          {docs.length}
        </Badge>
      </div>

      {canUpload && (
        <div className="mb-5 rounded-md border border-slate-800 bg-slate-950 p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto]">
            <div>
              <Label className="text-slate-300 text-xs">Document Type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="mt-1 border-slate-800 bg-slate-900 text-slate-50" data-testid="doc-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                  {DOC_TYPES.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Name / Label (optional)</Label>
              <input
                type="text"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder={`e.g. "John's contract - 123 Main"`}
                className="mt-1 w-full rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                data-testid="doc-label-input"
              />
            </div>
            <div className="flex items-end">
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPT}
                onChange={upload}
                disabled={busy}
                className="hidden"
                data-testid="doc-file-input"
              />
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="w-full bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60"
                data-testid="doc-upload-btn"
              >
                <Upload className="mr-2 h-4 w-4" />
                {busy ? "Uploading…" : "Upload"}
              </Button>
            </div>
          </div>
          <div className="mt-2 text-[11px] text-slate-500">
            Accepted: PDF, PNG, JPG, HEIC · Max 15MB
          </div>
        </div>
      )}

      <div className="space-y-2">
        {docs.length === 0 && (
          <div className="rounded-md border border-dashed border-slate-800 p-6 text-center text-sm text-slate-500">
            No documents yet.
          </div>
        )}
        {docs.map((d) => (
          <div
            key={d.document_id}
            className="flex items-center gap-3 rounded-md border border-slate-800 bg-slate-950 p-3"
            data-testid={`doc-${d.document_id}`}
          >
            <div className="flex h-9 w-9 items-center justify-center rounded border border-slate-800 bg-slate-900 text-emerald-400">
              <FileText className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-slate-100">
                {d.label?.trim() ? d.label : d.original_filename}
              </div>
              {d.label?.trim() && (
                <div className="truncate text-[11px] text-slate-500">{d.original_filename}</div>
              )}
              <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-300 border-emerald-500/20 text-[10px]">
                  {LABEL[d.doc_type] || d.doc_type}
                </Badge>
                <span>{(d.size / 1024).toFixed(1)} KB</span>
                <span>·</span>
                <span>{fmtDateTime(d.created_at)}</span>
                {d.uploaded_by_name && (
                  <span className="hidden sm:inline">
                    · by {d.uploaded_by_name}
                    {d.uploaded_by_role && d.uploaded_by_role !== "borrower" ? ` (${d.uploaded_by_role})` : ""}
                  </span>
                )}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => download(d)}
              className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
              data-testid={`doc-download-${d.document_id}`}
            >
              <Download className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}
